import axios from 'axios';
import { getAuthToken, getAuthHeader } from './authService';
import { getApiUrl } from '../utils/config';

// Interface cho dữ liệu thống kê theo nhóm
export interface TeamData {
  teamId: number | null;
  teamName: string;
  timePeriod: string;
  startDate: string;
  endDate: string;
  totalActivities: number;
  totalDistance: number;
  totalElevation: number;
  totalHours: number;
  totalMovingTime: number;
  distanceByAthlete: Record<string, number>;
  activitiesByType: Record<string, number>;
  distanceByDay: Record<string, number>;
  topAthletes: Array<{
    athleteName: string;
    distance: number;
    activities: number | null;
    elevation: number | null;
    movingTime: number | null;
  }>;
  members?: Array<{
    athleteName: string;
    totalActivities: number;
    totalDistance: number;
    totalMovingTime: number;
    averageSpeed: number;
    averagePace: number;
    firstname?: string;
    lastname?: string;
    fullname?: string;
    id?: number;
    username?: string;
  }>;
}

// Interface cho thông tin giải đấu
export interface Tournament {
  id: number;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  status: string;
  visible: boolean;
}

// Interface cho thông tin đội
export interface Team {
  id: number;
  name: string;
  description: string;
  isPrivate: boolean;
  type: string;
  tournamentId: number;
  tournamentName: string;
  adminId: number;
  adminUsername: string;
  createdAt: string;
  membersCount: number;
}

// Interface cho thông tin thành viên
export interface TeamMember {
  id: number;
  username: string;
  firstname: string;
  lastname: string;
  fullname: string;
  email: string;
  avatarUrl: string | null;
}

// Interface cho thông tin thành viên chi tiết
export interface MemberDetail {
  id: number;
  firstname: string;
  lastname: string;
  membership: string;
  isAdmin: boolean;
  isOwner: boolean;
  isFromActivity: boolean;
  createdAt: string;
  updatedAt: string;
}

// Interface cho hoạt động của thành viên
export interface MemberActivity {
  id: number;
  tournamentId: number;
  tournamentName: string;
  teamId: number;
  teamName: string;
  athleteFirstname: string;
  athleteLastname: string;
  athleteFullname: string;
  activityName: string;
  distance: number;
  movingTime: number;
  elapsedTime: number;
  totalElevationGain: number;
  activityType: string;
  sportType: string;
  workoutType: string | null;
  activityDate: string;
  syncedAt: string;
}

// Interface cho kết quả API hoạt động của thành viên theo khoảng thời gian
export interface MemberActivitiesResponse {
  startDate: string;
  endDate: string;
  activities: MemberActivity[];
  member: MemberDetail;
  totalMovingTime: number;
  totalActivities: number;
  totalDistance: number;
  totalDistanceFormatted: string;
  totalMovingTimeFormatted: string;
}

export interface Activity {
  id: number;
  stravaActivityId: number;
  userId: number;
  userName: string;
  name: string;
  type: string;
  sportType: string;
  distance: number;
  movingTime: number;
  elapsedTime: number;
  totalElevationGain: number;
  startDate: number[];
  startDateLocal: number[];
  timezone: string;
  averageSpeed: number;
  maxSpeed: number;
  averageHeartrate: number | null;
  maxHeartrate: number | null;
  sufferScore: number | null;
  hasHeartrate: boolean;
  locationCity: string | null;
  locationCountry: string | null;
  summaryPolyline: string | null;
  createdAt: number[] | null;
  updatedAt: number[] | null;
  suspiciousActivity?: number;
}

export interface UserActivitiesResponse {
  result: string;
  message: string;
  data: Activity[];
}

export interface ActivityDetail {
  id: number;
  stravaActivityId: number;
  userId: number;
  userName: string;
  userFirstName: string;
  userLastName: string;
  userProfileImageUrl: string;
  name: string;
  type: string;
  sportType: string;
  distance: number;
  movingTime: number;
  elapsedTime: number;
  totalElevationGain: number;
  startDate: number[];
  startDateLocal: number[];
  timezone: string;
  averageSpeed: number;
  maxSpeed: number;
  averageHeartrate: number | null;
  maxHeartrate: number | null;
  sufferScore: number | null;
  hasHeartrate: boolean;
  locationCity: string | null;
  locationCountry: string | null;
  summaryPolyline: string;
  teamId: number | null;
  teamName: string | null;
  teamImageUrl: string | null;
  tournamentId: number | null;
  tournamentName: string | null;
  tournamentImageUrl: string | null;
  createdAt: number[] | null;
  updatedAt: number[] | null;
  suspiciousActivity?: number;
}

export interface ActivityDetailResponse {
  content: ActivityDetail[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    unpaged: boolean;
    paged: boolean;
  };
  last: boolean;
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  first: boolean;
  numberOfElements: number;
  empty: boolean;
}

export interface ActivityDetailsResponse {
  result: string;
  message: string;
  data: ActivityDetailResponse;
}

/**
 * Lấy danh sách giải đấu
 * @returns Danh sách giải đấu
 */
export const getTournaments = async (): Promise<Tournament[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl('/api/tournaments'), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (response.data.result === 'SUCCESS') {
      // Trả về tất cả giải đấu, không lọc visible
      // vì ở các trang khác ngoài trang Tournament, chúng ta vẫn cần hiển thị đầy đủ
      return response.data.data;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching tournaments:', error);
    return [];
  }
};

/**
 * Trả về dữ liệu mẫu cho danh sách giải đấu
 */
// const getMockTournaments = (): Tournament[] => {
//   return [
//     {
//       id: 1,
//       name: "Giải chạy mùa xuân 2025",
//       description: "Giải chạy trong giai đoạn mùa xuân",
//       startDate: "2025-03-01",
//       endDate: "2025-03-31",
//       status: "ACTIVE"
//     },
//     {
//       id: 2,
//       name: "Giải đạp xe mùa hè 2025",
//       description: "Giải đạp xe trong giai đoạn mùa hè",
//       startDate: "2025-06-01",
//       endDate: "2025-06-30",
//       status: "UPCOMING"
//     },
//     {
//       id: 3,
//       name: "Marathon Hà Nội 2025",
//       description: "Giải chạy marathon thường niên tại Hà Nội",
//       startDate: "2025-05-01", 
//       endDate: "2025-05-02",
//       status: "UPCOMING"
//     }
//   ];
// };

/**
 * Bổ sung dữ liệu thiếu từ API, đảm bảo dữ liệu trả về luôn đầy đủ trường
 * @param data Dữ liệu từ API
 * @returns Dữ liệu đã được bổ sung đầy đủ
 */
const enrichTeamData = (data: any): TeamData => {
  // Đảm bảo members luôn tồn tại
  const members = data.members || [];
  
  // Nếu có topAthletes nhưng không có members, tạo members từ topAthletes
  if (!data.members && data.topAthletes && Array.isArray(data.topAthletes) && data.topAthletes.length > 0) {
    data.topAthletes.forEach((athlete: any) => {
      if (athlete.athleteName) {
        members.push({
          athleteName: athlete.athleteName,
          totalActivities: athlete.activities || 0,
          totalDistance: athlete.distance || 0,
          totalMovingTime: athlete.movingTime || 0,
          averageSpeed: 0,
          averagePace: 0
        });
      }
    });
  }
  
  // Đảm bảo totalMovingTime luôn tồn tại (chuyển đổi từ totalHours nếu cần)
  const totalMovingTime = data.totalMovingTime || (data.totalHours ? data.totalHours * 60 * 60 : 0);
  
  return {
    teamId: data.teamId || null,
    teamName: data.teamName || "Không xác định",
    timePeriod: data.timePeriod || "all",
    startDate: data.startDate || "",
    endDate: data.endDate || "",
    totalActivities: data.totalActivities || 0,
    totalDistance: data.totalDistance || 0,
    totalElevation: data.totalElevation || 0,
    totalHours: data.totalHours || 0,
    totalMovingTime: totalMovingTime,
    distanceByAthlete: data.distanceByAthlete || {},
    activitiesByType: data.activitiesByType || {},
    distanceByDay: data.distanceByDay || {},
    topAthletes: data.topAthletes || [],
    members: members
  };
};

/**
 * Lấy dữ liệu thống kê của giải đấu
 * @param tournamentId ID của giải đấu
 * @returns Dữ liệu thống kê của giải đấu
 */
export const getTournamentStatistics = async (tournamentId: number): Promise<Record<string, TeamData>> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl(`/api/club-activities/tournament/${tournamentId}/statistics`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Log cấu trúc data để debug
    console.log('API tournament statistics raw response:', response.data);
    
    // Kiểm tra cấu trúc dữ liệu phản hồi
    if (response.data) {
      // Trường hợp API trả về cấu trúc { result, message, data }
      if (response.data.result === "SUCCESS" && response.data.data) {
        // Đây là cấu trúc dữ liệu mới
        const stats = response.data.data;
        console.log('API stats data structure:', stats);

        // Chuyển đổi dữ liệu từ format mới sang format cũ để tương thích với UI
        const result: Record<string, TeamData> = {
          OVERALL: enrichTeamData({
            teamId: null,
            teamName: "Tổng hợp",
            timePeriod: "all",
            startDate: "",
            endDate: "",
            totalActivities: stats.totalActivities || 0,
            totalDistance: stats.totalDistance || 0,
            totalElevation: 0,
            totalHours: 0,
            totalMovingTime: stats.totalMovingTime || 0,
            distanceByAthlete: stats.distanceByAthlete || {},
            activitiesByType: {},
            distanceByDay: {},
            topAthletes: Object.keys(stats.distanceByAthlete || {}).map(name => ({
              athleteName: name,
              distance: stats.distanceByAthlete[name] || 0,
              activities: stats.activitiesByAthlete ? stats.activitiesByAthlete[name] || 0 : 0,
              elevation: null,
              movingTime: null
            }))
          })
        };
        
        // Thêm dữ liệu theo đội
        if (stats.distanceByTeam) {
          Object.keys(stats.distanceByTeam).forEach(teamName => {
            result[teamName] = enrichTeamData({
              teamId: null,
              teamName: teamName,
              timePeriod: "all",
              startDate: "",
              endDate: "",
              totalActivities: stats.activitiesByTeam ? stats.activitiesByTeam[teamName] || 0 : 0,
              totalDistance: stats.distanceByTeam[teamName] || 0,
              totalMovingTime: stats.movingTimeByTeam ? stats.movingTimeByTeam[teamName] || 0 : 0
            });
          });
        }
        
        console.log('Processed tournament statistics:', result);
        return result;
      }

      // Trường hợp API trả về cấu trúc cũ trực tiếp
      const result: Record<string, TeamData> = {};
      
      // Đảm bảo mỗi đội đều có dữ liệu đầy đủ
      if (typeof response.data === 'object') {
        Object.keys(response.data).forEach(key => {
          result[key] = enrichTeamData(response.data[key]);
        });
      }
      
      console.log('Processed tournament statistics (direct format):', result);
      return result;
    }
    
    throw new Error('Không thể lấy thông tin thống kê giải đấu');
  } catch (error: any) {
    console.error('Lỗi khi lấy dữ liệu thống kê giải đấu:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy thông tin thống kê giải đấu');
  }
};

/**
 * Lấy thống kê giải đấu theo khoảng thời gian
 * @param tournamentId ID của giải đấu
 * @param startDateLocal Ngày bắt đầu định dạng YYYY-MM-DD
 * @param endDate Ngày kết thúc định dạng YYYY-MM-DD
 * @returns Dữ liệu thống kê của giải đấu trong khoảng thời gian
 */
export const getTournamentStatisticsByDateRange = async (
  tournamentId: number,
  startDateLocal: string,
  endDate: string
): Promise<Record<string, TeamData>> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    // Format dates if they don't have time component
    const formattedStartDate = startDateLocal.includes('T') 
      ? startDateLocal 
      : `${startDateLocal}T00:00:00`;
    
    const formattedEndDate = endDate.includes('T') 
      ? endDate 
      : `${endDate}T23:59:59`;
    
    const response = await axios.get(
      getApiUrl(`/api/club-activities/tournament/${tournamentId}/date-range?startDateLocal=${encodeURIComponent(formattedStartDate)}&endDate=${encodeURIComponent(formattedEndDate)}`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Log cấu trúc data để debug
    console.log(`API tournament statistics by date range (${startDateLocal} - ${endDate}) raw response:`, response.data);
    
    // Kiểm tra cấu trúc dữ liệu phản hồi
    if (response.data) {
      // Trường hợp API trả về cấu trúc { result, message, data }
      if (response.data.result === "SUCCESS" && response.data.data) {
        // Đây là cấu trúc dữ liệu mới
        const stats = response.data.data;
        console.log('API stats by date range data structure:', stats);

        // Chuyển đổi dữ liệu từ format mới sang format cũ để tương thích với UI
        const result: Record<string, TeamData> = {
          OVERALL: enrichTeamData({
            teamId: null,
            teamName: "Tổng hợp",
            timePeriod: "custom",
            startDate: startDateLocal,
            endDate: endDate,
            totalActivities: stats.totalActivities || 0,
            totalDistance: stats.totalDistance || 0,
            totalElevation: 0,
            totalHours: 0,
            totalMovingTime: stats.totalMovingTime || 0,
            distanceByAthlete: stats.distanceByAthlete || {},
            activitiesByType: {},
            distanceByDay: {},
            topAthletes: Object.keys(stats.distanceByAthlete || {}).map(name => ({
              athleteName: name,
              distance: stats.distanceByAthlete[name] || 0,
              activities: stats.activitiesByAthlete ? stats.activitiesByAthlete[name] || 0 : 0,
              elevation: null,
              movingTime: null
            }))
          })
        };
        
        // Thêm dữ liệu theo đội
        if (stats.distanceByTeam) {
          Object.keys(stats.distanceByTeam).forEach(teamName => {
            result[teamName] = enrichTeamData({
              teamId: null,
              teamName: teamName,
              timePeriod: "custom",
              startDate: startDateLocal,
              endDate: endDate,
              totalActivities: stats.activitiesByTeam ? stats.activitiesByTeam[teamName] || 0 : 0,
              totalDistance: stats.distanceByTeam[teamName] || 0,
              totalMovingTime: stats.movingTimeByTeam ? stats.movingTimeByTeam[teamName] || 0 : 0
            });
          });
        }
        
        console.log('Processed tournament statistics by date range:', result);
        return result;
      }

      // Trường hợp API trả về hoạt động cụ thể
      if (response.data.result === "SUCCESS" && Array.isArray(response.data.data)) {
        console.log('API returns activities directly. Converting to statistics...');
        const activities = response.data.data;
        
        // Tạo thống kê từ danh sách hoạt động
        const statistics: Record<string, any> = {
          totalActivities: activities.length,
          totalDistance: 0,
          totalMovingTime: 0,
          distanceByAthlete: {},
          activitiesByAthlete: {},
          distanceByTeam: {},
          activitiesByTeam: {},
          movingTimeByTeam: {},
          distanceByDay: {}
        };
        
        // Tạo map cho thống kê theo thành viên và đội
        activities.forEach((activity: any) => {
          // Cập nhật tổng số
          statistics.totalDistance += (activity.distance || 0);
          statistics.totalMovingTime += (activity.movingTime || 0);
          
          // Cập nhật theo thành viên
          const athleteName = activity.athleteFullname || 
                              `${activity.athleteFirstname || ''} ${activity.athleteLastname || ''}`.trim();
          
          if (athleteName) {
            if (!statistics.distanceByAthlete[athleteName]) {
              statistics.distanceByAthlete[athleteName] = 0;
              statistics.activitiesByAthlete[athleteName] = 0;
            }
            statistics.distanceByAthlete[athleteName] += (activity.distance || 0);
            statistics.activitiesByAthlete[athleteName] += 1;
          }
          
          // Cập nhật theo đội
          const teamName = activity.teamName || 'Unknown';
          if (!statistics.distanceByTeam[teamName]) {
            statistics.distanceByTeam[teamName] = 0;
            statistics.activitiesByTeam[teamName] = 0;
            statistics.movingTimeByTeam[teamName] = 0;
          }
          statistics.distanceByTeam[teamName] += (activity.distance || 0);
          statistics.activitiesByTeam[teamName] += 1;
          statistics.movingTimeByTeam[teamName] += (activity.movingTime || 0);
          
          // Cập nhật theo ngày
          const activityDate = activity.activityDate 
            ? activity.activityDate.split('T')[0] 
            : null;
          
          if (activityDate) {
            if (!statistics.distanceByDay[activityDate]) {
              statistics.distanceByDay[activityDate] = 0;
            }
            statistics.distanceByDay[activityDate] += (activity.distance || 0);
          }
        });
        
        // Tạo kết quả như form đầu ra
        const result: Record<string, TeamData> = {
          OVERALL: enrichTeamData({
            teamId: null,
            teamName: "Tổng hợp",
            timePeriod: "custom",
            startDate: startDateLocal,
            endDate: endDate,
            totalActivities: statistics.totalActivities || 0,
            totalDistance: statistics.totalDistance || 0,
            totalMovingTime: statistics.totalMovingTime || 0,
            distanceByAthlete: statistics.distanceByAthlete || {},
            activitiesByType: {},
            distanceByDay: statistics.distanceByDay || {},
            topAthletes: Object.keys(statistics.distanceByAthlete || {}).map(name => ({
              athleteName: name,
              distance: statistics.distanceByAthlete[name] || 0,
              activities: statistics.activitiesByAthlete[name] || 0,
              elevation: null,
              movingTime: null
            }))
          })
        };
        
        // Thêm dữ liệu theo đội
        if (statistics.distanceByTeam) {
          Object.keys(statistics.distanceByTeam).forEach(teamName => {
            result[teamName] = enrichTeamData({
              teamId: null,
              teamName: teamName,
              timePeriod: "custom",
              startDate: startDateLocal,
              endDate: endDate,
              totalActivities: statistics.activitiesByTeam[teamName] || 0,
              totalDistance: statistics.distanceByTeam[teamName] || 0,
              totalMovingTime: statistics.movingTimeByTeam[teamName] || 0
            });
          });
        }
        
        console.log('Generated tournament statistics from activities:', result);
        return result;
      }

      // Trường hợp API trả về cấu trúc cũ trực tiếp
      const result: Record<string, TeamData> = {};
      
      // Đảm bảo mỗi đội đều có dữ liệu đầy đủ
      if (typeof response.data === 'object') {
        Object.keys(response.data).forEach(key => {
          result[key] = enrichTeamData({
            ...response.data[key],
            timePeriod: "custom",
            startDate: startDateLocal,
            endDate: endDate
          });
        });
      }
      
      console.log('Processed tournament statistics by date range (direct format):', result);
      return result;
    }
    
    throw new Error('Không thể lấy thông tin thống kê giải đấu');
  } catch (error: any) {
    console.error('Lỗi khi lấy dữ liệu thống kê giải đấu theo khoảng thời gian:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy thông tin thống kê giải đấu');
  }
};

/**
 * Lấy danh sách đội theo giải đấu
 * @param tournamentId ID của giải đấu
 * @returns Danh sách đội thuộc giải đấu
 */
export const getTeamsByTournament = async (tournamentId: number): Promise<Team[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl(`/api/teams/tournament/${tournamentId}`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (response.data && response.data.result === "SUCCESS") {
      return response.data.data || [];
    }
    
    return [];
  } catch (error: any) {
    console.error('Lỗi khi lấy danh sách đội theo giải đấu:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy danh sách đội');
  }
};

/**
 * Lấy thống kê hoạt động của đội
 * @param teamId ID của đội
 * @returns Dữ liệu thống kê hoạt động của đội
 */
export const getTeamStatistics = async (teamId: number): Promise<TeamData> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl(`/api/club-activities/team/${teamId}`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Log cấu trúc data để debug
    console.log(`API team statistics for team ${teamId} raw response:`, response.data);
    
    if (response.data) {
      // Trường hợp API trả về cấu trúc { result, message, data }
      if (response.data.result === "SUCCESS" && response.data.data) {
        const rawData = response.data.data;
        console.log('API team data structure:', rawData);
        
        // Xây dựng dữ liệu đội từ response
        const teamData: TeamData = {
          teamId: teamId,
          teamName: rawData.team?.name || "Không xác định",
          timePeriod: "all",
          startDate: "",
          endDate: "",
          totalActivities: rawData.totalActivities || 0,
          totalDistance: rawData.totalDistance || 0,
          totalElevation: 0,
          totalHours: 0,
          totalMovingTime: rawData.totalMovingTime || 0,
          distanceByAthlete: {},
          activitiesByType: {},
          distanceByDay: {},
          topAthletes: [],
          members: []
        };
        
        // Nếu có dữ liệu activities, xây dựng danh sách thành viên
        if (rawData.activities && Array.isArray(rawData.activities)) {
          // Tạo map để tổng hợp theo thành viên
          const memberStats: Record<string, any> = {};
          
          rawData.activities.forEach((activity: any) => {
            const name = activity.athleteFullname || `${activity.athleteFirstname || ''} ${activity.athleteLastname || ''}`.trim();
            if (name) {
              if (!memberStats[name]) {
                memberStats[name] = {
                  athleteName: name,
                  firstname: activity.athleteFirstname,
                  lastname: activity.athleteLastname,
                  fullname: activity.athleteFullname,
                  totalActivities: 0,
                  totalDistance: 0,
                  totalMovingTime: 0
                };
              }
              
              memberStats[name].totalActivities += 1;
              memberStats[name].totalDistance += (activity.distance || 0);
              memberStats[name].totalMovingTime += (activity.movingTime || 0);
            }
          });
          
          // Chuyển đổi map thành mảng và tính toán tốc độ và nhịp độ
          const members = Object.values(memberStats).map((member: any) => {
            // Tốc độ trung bình (m/s)
            const averageSpeed = member.totalMovingTime > 0 
              ? member.totalDistance / member.totalMovingTime 
              : 0;
            
            // Nhịp độ (seconds/km)
            const averagePace = member.totalDistance > 0 
              ? (member.totalMovingTime / (member.totalDistance / 1000)) 
              : 0;
            
            return {
              ...member,
              averageSpeed,
              averagePace
            };
          });
          
          teamData.members = members;
        }
        
        console.log('Processed team statistics:', teamData);
        return enrichTeamData(teamData);
      }
      
      // Trường hợp API trả về dữ liệu đội trực tiếp
      console.log('Direct team data format:', response.data);
      return enrichTeamData(response.data);
    }
    
    throw new Error('Không thể lấy thông tin thống kê đội');
  } catch (error: any) {
    console.error('Lỗi khi lấy dữ liệu thống kê đội:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy thông tin thống kê đội');
  }
};

/**
 * Lấy thống kê đội theo khoảng thời gian
 * @param teamId ID của đội
 * @param startDateLocal Ngày bắt đầu định dạng YYYY-MM-DD
 * @param endDate Ngày kết thúc định dạng YYYY-MM-DD
 * @returns Dữ liệu thống kê của đội trong khoảng thời gian
 */
export const getTeamStatisticsByDateRange = async (
  teamId: number,
  startDateLocal: string,
  endDate: string
): Promise<TeamData> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    // Format dates if they don't have time component
    const formattedStartDate = startDateLocal.includes('T') 
      ? startDateLocal 
      : `${startDateLocal}T00:00:00`;
    
    const formattedEndDate = endDate.includes('T') 
      ? endDate 
      : `${endDate}T23:59:59`;
    
    const response = await axios.get(
      getApiUrl(`/api/club-activities/team/${teamId}/date-range?startDateLocal=${encodeURIComponent(formattedStartDate)}&endDate=${encodeURIComponent(formattedEndDate)}`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log(`API team statistics by date range (${startDateLocal} - ${endDate}) raw response:`, response.data);
    
    if (response.data) {
      // Xử lý tương tự như hàm getTeamStatistics, có bổ sung thông tin khoảng thời gian
      let teamData: TeamData = await getTeamStatistics(teamId); // Lấy thông tin cơ bản của đội
      
      // Cập nhật các trường liên quan đến khoảng thời gian
      teamData = {
        ...teamData,
        timePeriod: "custom",
        startDate: startDateLocal,
        endDate: endDate
      };
      
      // Nếu API trả về cấu trúc { result, message, data } với thông tin cụ thể cho khoảng thời gian
      if (response.data.result === "SUCCESS" && response.data.data) {
        const rawData = response.data.data;
        
        // Cập nhật các trường thống kê
        teamData.totalActivities = rawData.totalActivities || 0;
        teamData.totalDistance = rawData.totalDistance || 0;
        teamData.totalMovingTime = rawData.totalMovingTime || 0;
        
        // Cập nhật danh sách thành viên nếu có
        if (rawData.activities && Array.isArray(rawData.activities)) {
          // Cập nhật tương tự như trong getTeamStatistics
          const memberStats: Record<string, any> = {};
          
          rawData.activities.forEach((activity: any) => {
            const name = activity.athleteFullname || `${activity.athleteFirstname || ''} ${activity.athleteLastname || ''}`.trim();
            if (name) {
              if (!memberStats[name]) {
                memberStats[name] = {
                  athleteName: name,
                  firstname: activity.athleteFirstname,
                  lastname: activity.athleteLastname,
                  fullname: activity.athleteFullname,
                  totalActivities: 0,
                  totalDistance: 0,
                  totalMovingTime: 0
                };
              }
              
              memberStats[name].totalActivities += 1;
              memberStats[name].totalDistance += (activity.distance || 0);
              memberStats[name].totalMovingTime += (activity.movingTime || 0);
            }
          });
          
          // Chuyển đổi map thành mảng và tính toán tốc độ và nhịp độ
          const members = Object.values(memberStats).map((member: any) => {
            const averageSpeed = member.totalMovingTime > 0 
              ? member.totalDistance / member.totalMovingTime 
              : 0;
            
            const averagePace = member.totalDistance > 0 
              ? (member.totalMovingTime / (member.totalDistance / 1000)) 
              : 0;
            
            return {
              ...member,
              averageSpeed,
              averagePace
            };
          });
          
          teamData.members = members;
        }
      }
      
      console.log('Processed team statistics by date range:', teamData);
      return enrichTeamData(teamData);
    }
    
    throw new Error('Không thể lấy thông tin thống kê đội');
  } catch (error: any) {
    console.error('Lỗi khi lấy dữ liệu thống kê đội theo khoảng thời gian:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy thông tin thống kê đội');
  }
};

/**
 * Lấy danh sách thành viên trong đội
 * @param teamId ID của đội
 * @returns Danh sách thành viên trong đội
 */
export const getTeamMembers = async (teamId: number): Promise<TeamMember[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    console.log(`Gọi API lấy thành viên, teamId = ${teamId}`);
    const response = await axios.get(
      getApiUrl(`/api/teams/${teamId}/members`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Log dữ liệu thô
    console.log('Dữ liệu thô API trả về:', response.data);
    
    // Kiểm tra cấu trúc dữ liệu và parse đúng cách
    let members: TeamMember[] = [];
    
    if (response.data && response.data.result === "SUCCESS") {
      members = response.data.data || [];
    } else if (Array.isArray(response.data)) {
      // Trường hợp API trả về trực tiếp là mảng
      members = response.data;
    } else if (response.data && Array.isArray(response.data.data)) {
      // Trường hợp khác - lấy data từ response
      members = response.data.data;
    }
    
    console.log('Danh sách thành viên đã parse:', members);
    console.log(`Số lượng thành viên: ${members.length}`);
    
    // Đảm bảo mỗi thành viên có fullname, nếu không thì tạo từ firstname và lastname
    members = members.map(member => {
      if (!member.fullname && member.firstname && member.lastname) {
        return {
          ...member,
          fullname: `${member.firstname} ${member.lastname}`
        };
      }
      return member;
    });
    
    return members;
  } catch (error: any) {
    console.error('Lỗi khi lấy danh sách thành viên trong đội:', error);
    
    if (error.response) {
      console.error('Chi tiết lỗi API:', error.response.status, error.response.data);
    }
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy danh sách thành viên');
  }
};

/**
 * Đồng bộ hoạt động của giải đấu theo ngày cụ thể
 * @param tournamentId ID của giải đấu
 * @param date Ngày cần đồng bộ theo định dạng YYYY-MM-DD
 * @returns Kết quả đồng bộ
 */
export const syncActivitiesByDay = async (tournamentId: number, date: string): Promise<any> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để thực hiện chức năng này');
    }
    
    const response = await axios.post(
      getApiUrl(`/api/club-activities/tournament/${tournamentId}/sync-day?date=${date}`),
      {}, // Empty body for POST request
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (response.data) {
      return response.data;
    }
    
    throw new Error('Không thể đồng bộ hoạt động theo ngày');
  } catch (error: any) {
    console.error('Lỗi khi đồng bộ hoạt động theo ngày:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể đồng bộ hoạt động theo ngày');
  }
};

/**
 * Lấy danh sách hoạt động của thành viên theo khoảng thời gian
 * @param memberId ID của thành viên
 * @param startDateLocal Ngày bắt đầu (định dạng YYYY-MM-DDThh:mm:ss)
 * @param endDate Ngày kết thúc (định dạng YYYY-MM-DDThh:mm:ss)
 * @returns Dữ liệu hoạt động của thành viên
 */
export const getMemberActivitiesByDateRange = async (
  memberId: number,
  startDateLocal: string,
  endDate: string
): Promise<MemberActivitiesResponse> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl(`/api/club-activities/member/${memberId}/date-range`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        params: {
          startDateLocal,
          endDate
        }
      }
    );
    
    // Kiểm tra cấu trúc dữ liệu phản hồi
    if (response.data && response.data.result === "SUCCESS" && response.data.data) {
      return response.data.data;
    }
    
    throw new Error('Không thể lấy thông tin hoạt động của thành viên');
  } catch (error: any) {
    console.error('Lỗi khi lấy dữ liệu hoạt động của thành viên:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy thông tin hoạt động của thành viên');
  }
};

export const getUserActivities = async (userId: number): Promise<Activity[]> => {
  try {
    console.log('Gọi getUserActivities với userId:', userId);
    const authHeader = getAuthHeader();
    console.log('Auth header:', authHeader);
    
    const apiUrl = `${getApiUrl()}/api/activities/users/${userId}`;
    console.log('URL API activities:', apiUrl);
    
    const response = await axios.get<UserActivitiesResponse>(
      apiUrl,
      {
        headers: {
          'Authorization': authHeader,
        }
      }
    );

    console.log('Kết quả API activities:', response.data);

    if (response.data.result === 'SUCCESS') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Không thể lấy danh sách hoạt động của người dùng');
    }
  } catch (error: any) {
    console.error('Lỗi chi tiết khi lấy danh sách hoạt động:', error);
    if (error.response) {
      console.error('Mã lỗi:', error.response.status);
      console.error('Dữ liệu lỗi:', error.response.data);
      const errorMessage = error.response.data.message || 'Lỗi khi lấy danh sách hoạt động';
      throw new Error(errorMessage);
    }
    throw error;
  }
};

/**
 * Lấy danh sách hoạt động của người dùng theo khoảng thời gian
 * @param userId ID của người dùng
 * @param startDateLocal Ngày bắt đầu (định dạng YYYY-MM-DDThh:mm:ss)
 * @param endDate Ngày kết thúc (định dạng YYYY-MM-DDThh:mm:ss)
 * @returns Danh sách hoạt động của người dùng
 */
export const getUserActivitiesByDateRange = async (
  userId: number,
  startDateLocal: string,
  endDate: string
): Promise<Activity[]> => {
  try {
    console.log(`Gọi getUserActivitiesByDateRange với userId: ${userId}, startDateLocal: ${startDateLocal}, endDate: ${endDate}`);
    const authHeader = getAuthHeader();
    
    const apiUrl = `${getApiUrl()}/api/activities/users/${userId}/date-range`;
    console.log('URL API activities by date range:', apiUrl);
    
    const response = await axios.get<UserActivitiesResponse>(
      apiUrl,
      {
        headers: {
          'Authorization': authHeader,
        },
        params: {
          startDateLocal,
          endDate
        }
      }
    );

    console.log('Kết quả API activities by date range:', response.data);

    if (response.data.result === 'SUCCESS') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Không thể lấy danh sách hoạt động của người dùng theo khoảng thời gian');
    }
  } catch (error: any) {
    console.error('Lỗi chi tiết khi lấy danh sách hoạt động theo khoảng thời gian:', error);
    if (error.response) {
      console.error('Mã lỗi:', error.response.status);
      console.error('Dữ liệu lỗi:', error.response.data);
      const errorMessage = error.response.data.message || 'Lỗi khi lấy danh sách hoạt động theo khoảng thời gian';
      throw new Error(errorMessage);
    }
    throw error;
  }
};

/**
 * Lấy danh sách hoạt động chi tiết với các bộ lọc
 * @param params Tham số lọc
 * @returns Danh sách hoạt động chi tiết
 */
export const getActivityDetails = async (params: {
  startDateLocal?: string;
  endDateLocal?: string;
  teamId?: number | null;
  tournamentId?: number | null;
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: string;
}): Promise<ActivityDetailsResponse> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin hoạt động');
    }
    
    // Tạo query params từ đối tượng params
    const queryParams = new URLSearchParams();
    
    if (params.startDateLocal) queryParams.append('startDateLocal', params.startDateLocal);
    if (params.endDateLocal) queryParams.append('endDateLocal', params.endDateLocal);
    if (params.teamId) queryParams.append('teamId', params.teamId.toString());
    if (params.tournamentId) queryParams.append('tournamentId', params.tournamentId.toString());
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortDir) queryParams.append('sortDir', params.sortDir);
    
    const response = await axios.get(
      `${getApiUrl('/api/activities/details')}?${queryParams.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error: any) {
    console.error('Lỗi khi lấy danh sách hoạt động:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw error;
  }
};

/**
 * Cập nhật trạng thái đáng nghi ngờ cho hoạt động (chỉ ADMIN)
 * @param activityId ID của hoạt động cần cập nhật
 * @param status Trạng thái mới (0: không đáng nghi ngờ, 1: đáng nghi ngờ)
 * @returns Kết quả cập nhật với thông tin hoạt động đã cập nhật
 */
export const updateActivitySuspiciousStatus = async (activityId: number, status: 0 | 1): Promise<any> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để thực hiện chức năng này');
    }
    
    const response = await axios.put(
      getApiUrl(`/api/activities/${activityId}/suspicious/${status}`),
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error: any) {
    console.error('Lỗi khi cập nhật trạng thái hoạt động đáng nghi ngờ:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể cập nhật trạng thái hoạt động');
  }
};

/**
 * Cập nhật trạng thái đáng nghi ngờ cho nhiều hoạt động cùng lúc (chỉ ADMIN)
 * @param activityIds Danh sách ID của các hoạt động cần cập nhật
 * @param status Trạng thái mới (0: không đáng nghi ngờ, 1: đáng nghi ngờ)
 * @returns Kết quả cập nhật với số lượng hoạt động đã cập nhật thành công
 */
export const bulkUpdateActivitySuspiciousStatus = async (activityIds: number[], status: 0 | 1): Promise<any> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để thực hiện chức năng này');
    }
    
    // Thực hiện nhiều lần gọi API cập nhật song song
    const updatePromises = activityIds.map(activityId => 
      axios.put(
        getApiUrl(`/api/activities/${activityId}/suspicious/${status}`),
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )
    );
    
    const results = await Promise.allSettled(updatePromises);
    
    // Đếm số lượng cập nhật thành công
    const successCount = results.filter(result => result.status === 'fulfilled').length;
    
    // Nếu không có cập nhật thành công nào, ném lỗi
    if (successCount === 0) {
      throw new Error('Không thể cập nhật trạng thái cho bất kỳ hoạt động nào');
    }
    
    return {
      result: 'SUCCESS',
      message: `Đã cập nhật thành công ${successCount}/${activityIds.length} hoạt động`,
      data: {
        successCount,
        totalCount: activityIds.length
      }
    };
  } catch (error: any) {
    console.error('Lỗi khi cập nhật hàng loạt trạng thái hoạt động đáng nghi ngờ:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể cập nhật trạng thái hoạt động');
  }
};

/**
 * Lấy danh sách hoạt động đáng ngờ theo giải đấu
 * @param tournamentId ID của giải đấu
 * @param params Tham số lọc
 * @returns Danh sách hoạt động đáng ngờ
 */
export const getSuspiciousActivities = async (tournamentId: number, params: {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: string;
  startDate?: string;
  endDate?: string;
}): Promise<ActivityDetailsResponse> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin hoạt động đáng ngờ');
    }
    
    // Tạo query params từ đối tượng params
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortDir) queryParams.append('sortDir', params.sortDir);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    
    const response = await axios.get(
      `${getApiUrl(`/api/activities/suspicious/tournaments/${tournamentId}`)}?${queryParams.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error: any) {
    console.error('Lỗi khi lấy danh sách hoạt động đáng ngờ:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw error;
  }
}; 